import { useEffect, useMemo, useRef, useState } from "react";
import { geoMercator, geoPath } from "d3-geo";
import iranProvincesUrl from "../data/iran-provinces.geojson?url";
import provinceBoundaryAliases from "../data/province-boundary-aliases.json";
import { fa } from "../i18n/fa";
import { persianDisplayText } from "@/lib/persianUi";
import { formatNumber, formatPercent } from "@/features/reports/lib/format";

const DEFAULT_SIZE = { width: 720, height: 540 };
const EMPTY_FILL = "#eef1f3";
const LOW_FILL = [232, 245, 239];
const HIGH_FILL = [0, 105, 92];

function provinceFill(value, maximum) {
  if (!value || maximum <= 0) return EMPTY_FILL;
  const ratio = Math.sqrt(Math.max(0, value) / maximum);
  const rgb = LOW_FILL.map((low, index) =>
    Math.round(low + (HIGH_FILL[index] - low) * ratio),
  );
  return `rgb(${rgb.join(", ")})`;
}

export default function IranProvinceMap({
  provinces,
  totalKg,
  selectedCode,
  onSelect,
}) {
  const containerRef = useRef(null);
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [geometry, setGeometry] = useState(null);
  const [mapError, setMapError] = useState(false);

  useEffect(() => {
    let live = true;
    fetch(iranProvincesUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`Province map request failed: ${response.status}`);
        return response.json();
      })
      .then((data) => live && setGeometry(data))
      .catch(() => live && setMapError(true));
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) {
        setSize({ width: Math.round(width), height: Math.round(height) });
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const projectionPath = useMemo(() => {
    if (!geometry) return null;
    const projection = geoMercator().fitExtent(
      [[12, 12], [size.width - 12, size.height - 12]],
      geometry,
    );
    return geoPath(projection);
  }, [geometry, size]);
  const provinceByCode = useMemo(
    () => new Map(provinces.map((province) => [province.code, province])),
    [provinces],
  );
  const maximum = Math.max(0, ...provinces.map((province) => province.kg));

  useEffect(() => {
    if (!geometry) return;
    for (const feature of geometry.features) {
      const sourceName = feature.properties.sourceName;
      if (!provinceBoundaryAliases[sourceName]) {
        console.error(`[traceability] Unmatched Iran province boundary: ${sourceName}`);
      }
    }
  }, [geometry]);

  if (mapError) return <div className="tr-empty"><p>{fa.geo.mapError}</p></div>;
  if (!projectionPath || !geometry) return <div className="tr-empty"><p>{fa.geo.mapLoading}</p></div>;

  return (
    <>
      <div className="tr-geo-map-wrap" ref={containerRef}>
        <svg
          className="tr-geo-map"
          viewBox={`0 0 ${size.width} ${size.height}`}
          role="group"
          aria-label={fa.geo.mapLabel}
        >
          {geometry.features.map((feature) => {
            const code = provinceBoundaryAliases[feature.properties.sourceName];
            const province = provinceByCode.get(code);
            if (!province) return null;

            const selected = province.code === selectedCode;
            const share = totalKg > 0 ? (province.kg / totalKg) * 100 : 0;
            const tooltip = `${persianDisplayText(province.name, "نامشخص")} · ${fa.geo.kg(province.kg)} · ${fa.geo.share(formatPercent(share))}`;
            return (
              <path
                key={province.code}
                d={projectionPath(feature) || ""}
                className={`tr-geo-province${selected ? " selected" : ""}`}
                style={{ fill: provinceFill(province.kg, maximum) }}
                role="button"
                tabIndex={0}
                aria-label={tooltip}
                aria-pressed={selected}
                onClick={() => onSelect(selected ? null : province.code)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect(selected ? null : province.code);
                  }
                }}
              >
                <title>{tooltip}</title>
              </path>
            );
          })}
        </svg>
      </div>
      <div className="tr-geo-legend">
        <span>{fa.geo.low(formatNumber(0))}</span>
        <span className="tr-geo-legend-scale" />
        <span>{fa.geo.high(formatNumber(maximum))}</span>
      </div>
    </>
  );
}
